import React, { useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// FAQ Data
const helpData = {
  TPIN: [
    {
      question: "What is TPIN?",
      answer: `• Transaction Personal Identification Number (TPIN) is a 4 digit secret code that can be used as an additional security layer for your wallet transaction.

• TPIN can be set at the time of registration with TA Wallet or later from “Profile” section.

• TPIN is used to authenticate all financial transactions.

Note: Please keep TPIN as a secret code and do not share it with anyone.`,
    },
    {
      question: "How do I set the TPIN for my wallet?",
      answer: `If you have not set TPIN at the time of registration, you can set it through the Gear Bar (≡) on the top left corner.

Steps:
• Select Profile → TPIN
• Enter TPIN
• Choose a security question

Note: Please keep TPIN as a secret code and do not share it with anyone.`,
    },
    {
      question: "How do I change my TPIN?",
      answer: `It's easy!

• Go to Gear Bar (≡) on the top left corner → Profile
• Select "TPIN: Change TPIN"
• Enter your old TPIN
• Create new TPIN by giving a security question and answer`,
    },
    {
      question: "I have forgot my TPIN, help!",
      answer: `Don’t worry! You can reset it.

Steps:
• Go to Gear Bar (≡) on the top left corner
• Select "TPIN: Forgot TPIN?"
• Enter your security question and answer
• Set your new TPIN`,
    },
    {
      question: "My TPIN is not working? What do I do?",
      answer: `• Please re-try the entry as there might be connectivity issues.

• If it still doesn't work, change your TPIN as explained above.

• Still having issues? Contact customer support.`,
    },
    {
      question: "What happens if I enter wrong TPIN during a transaction?",
      answer: `No problem. The app will prompt you to re-enter the correct TPIN.

You will be allowed a maximum number of tries.`,
    },
  ],
};

const TPIN = ({ selectedSection = "TPIN" }) => {
  const questions = helpData[selectedSection] || [];
  const [openIndex, setOpenIndex] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const toggleAnswer = (index) =>
    setOpenIndex((prev) => (prev === index ? null : index));

  return (
    <Box width="100%">
      {/* Banner */}
      <Box
        sx={{
          width: "100%",
          height: { xs: 180, sm: 220, md: 260 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 1, sm: 3 },
        }}
      >
        <Box
          component="img"
          src="/Assets/Tpin.jpg"
          alt="TPIN banner"
          sx={{
            width: "100%",
            maxWidth: 1000,
            height: "100%",
            objectFit: "cover",
            borderRadius: "0 0 10px 10px",
          }}
        />
      </Box>

      {/* Title */}
      <Box mt={3} display="flex" justifyContent="center">
        <Typography
          variant={isMobile ? "h6" : "h5"}
          fontWeight="bold"
          color="black"
        >
          TPIN
        </Typography>
      </Box>

      {/* FAQ Section */}
      <Box
        maxWidth="900px"
        mx="auto"
        px={{ xs: 1.5, sm: 2, md: 3 }}
        mt={2}
        py={2}
      >
        <List disablePadding>
          {questions.map((item, index) => (
            <Box
              key={index}
              sx={{
                border: "1px solid #ddd",
                borderRadius: 2,
                mb: 2,
                overflow: "hidden",
                boxShadow:
                  openIndex === index ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
                transition: "box-shadow 0.2s ease-in-out",
              }}
            >
              <ListItemButton
                onClick={() => toggleAnswer(index)}
                sx={{
                  borderBottom: "1px solid #eee",
                  px: 2,
                  py: 1.5,
                  backgroundColor:
                    openIndex === index ? "#e0f7fa" : "transparent",
                  "&:hover": {
                    backgroundColor:
                      openIndex === index ? "#b2ebf2" : "#f9f9f9",
                  },
                }}
              >
                <ListItemText primary={item.question} />
                {openIndex === index ? (
                  <ExpandMoreIcon sx={{ fontSize: 18 }} />
                ) : (
                  <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
                )}
              </ListItemButton>

              <Collapse in={openIndex === index} timeout="auto" unmountOnExit>
                <Box px={3} py={2} bgcolor="#fafafa">
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
                  >
                    {item.answer}
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          ))}
        </List>
      </Box>
    </Box>
  );
};

export default TPIN;
